import React from 'react';
import i18n from '@/utils/i18n';
import { ApolloError } from '@apollo/client';
import { Table } from 'reactstrap';

interface TableProps {
  headers: string[];
  rows: any[];
  loading: boolean;
  error: ApolloError | undefined;
  striped: boolean;
  hover: boolean;
  bordered?: boolean;
  headerElements?: any[];
  stickyFirstCols?: number;
}

export const CustomTable: React.FC<TableProps> = ({
  headers,
  rows,
  loading,
  error,
  striped,
  hover,
  bordered = false,
  headerElements = null,
  stickyFirstCols = 0,
}) => {
  if (error != null) {
    console.log(error);
  }

  // Generate sticky column styles
  const getStickyStyle = (colIndex: number) => {
    if (colIndex >= stickyFirstCols) return {};

    // Calculate left position based on previous columns
    // Using approximate widths - in a real implementation you might want to measure actual widths
    const baseWidth = 150; // Base width for each sticky column
    const left = colIndex * baseWidth;

    return {
      position: 'sticky' as const,
      left: `${left}px`,
      backgroundColor: 'white',
      zIndex: 10,
      borderRight: bordered ? '1px solid #dee2e6' : undefined,
    };
  };

  const tableStyle =
    stickyFirstCols > 0
      ? {
          borderCollapse: 'separate' as const,
          borderSpacing: 0,
        }
      : {};

  return (
    <div style={{ overflowX: 'auto', width: '100%' }}>
      <Table
        striped={striped}
        hover={hover}
        bordered={bordered}
        style={tableStyle}
        responsive
      >
        <thead>
          <tr>
            {headerElements != null &&
              headerElements.map((element, index) =>
                React.cloneElement(element, {
                  key: index,
                  style: { ...element.props.style, ...getStickyStyle(index) },
                }),
              )}
            {headerElements == null &&
              headers.map((header, index) => (
                <th key={header} style={getStickyStyle(index)}>
                  {header}
                </th>
              ))}
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={headers.length}>{i18n.t('Common:loading')}</td>
            </tr>
          )}
          {error && (
            <tr>
              <td colSpan={headers.length}>{i18n.t('Common:error')}</td>
            </tr>
          )}
          {!loading && !error && rows.length === 0 && (
            <tr>
              <td colSpan={headers.length}>{i18n.t('Common:noItems')}</td>
            </tr>
          )}
          {!loading &&
            !error &&
            rows.length > 0 &&
            rows.map((row, rowIndex) => {
              // Clone the row element and apply sticky styles to first n cells
              if (React.isValidElement(row)) {
                const cells = React.Children.toArray(
                  (row.props as any).children,
                );
                const updatedCells = cells.map((cell, cellIndex) => {
                  if (
                    React.isValidElement(cell) &&
                    cellIndex < stickyFirstCols
                  ) {
                    return React.cloneElement(cell as any, {
                      key: cellIndex,
                      style: {
                        ...(cell.props as any)?.style,
                        ...getStickyStyle(cellIndex),
                      },
                    });
                  }
                  return cell;
                });

                return React.cloneElement(row as any, {
                  key: rowIndex,
                  children: updatedCells,
                });
              }
              return row;
            })}
        </tbody>
      </Table>
    </div>
  );
};
